import React, { useState } from 'react';
import { CalendarDays, Download, Info, AlertCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика платежей по налогам самозанятого
 */
const PaymentsSchedule = ({ schedule, results, formData, showFullSchedule, setShowFullSchedule }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первых 12 месяцев, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, Math.min(12, schedule.length));

  // Расчет итоговых сумм
  const totals = {
    income: schedule.reduce((sum, item) => sum + item.income, 0),
    expenses: schedule.reduce((sum, item) => sum + item.expenses, 0),
    taxAmount: schedule.reduce((sum, item) => sum + item.taxAmount, 0),
    insurance: schedule.reduce((sum, item) => sum + item.insurance, 0),
    totalPayment: schedule.reduce((sum, item) => sum + item.totalPayment, 0),
    deduction: schedule.reduce((sum, item) => sum + item.deduction, 0)
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <CalendarDays className="w-5 h-5 mr-2 text-indigo-500" />
          График платежей ({schedule.length} {schedule.length === 1 ? 'мес.' : schedule.length < 5 ? 'мес.' : 'мес.'})
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 12 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} мес.`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий график
        </button>
        <button
          onClick={() => setActiveTab('payment')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'payment'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          График платежей
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          {results.taxSystem === 'npd' ? (
            <p>
              Для самозанятых (НПД) налог рассчитывается ежемесячно и должен быть уплачен до 25-го числа следующего месяца. 
              ФНС формирует квитанцию автоматически на основании данных о поступлениях.
            </p>
          ) : (
            <p>
              Для УСН налоговый период – календарный год. Авансовые платежи уплачиваются ежеквартально не позднее 25-го числа первого месяца, 
              следующего за истекшим отчетным периодом. Годовой налог – не позднее 25 апреля года, следующего за истекшим налоговым периодом.
            </p>
          )}
        </div>
      </div>

      {/* Опция предупреждения для НПД */}
      {results.taxSystem === 'npd' && results.incomeAmount > 2400000 / results.periodMonths * 12 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded p-3 mb-4 flex items-start">
          <AlertCircle className="text-red-500 dark:text-red-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700 dark:text-red-300">
            <p>
              <strong>Внимание!</strong> При текущем уровне дохода вы превысите годовой лимит в 2,4 млн ₽ для самозанятых. 
              При превышении лимита вам придется перейти на другой налоговый режим.
            </p>
          </div>
        </div>
      )}

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] payment-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <tbody className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Месяц</th>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Год</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Доход</th>
                  {formData.hasExpenses && (
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Расходы</th>
                  )}
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Налоговая база</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Налог</th>
                  {results.deduction > 0 && (
                    <th scope="col" className="px-3 py-2 font-semibold text-right">Вычет</th>
                  )}
                  {(formData.includeInsurance || formData.fiscalRole === 'entrepreneur') && (
                    <td className="px-3 py-2 whitespace-nowrap text-right text-amber-600 dark:text-amber-400">{formatCurrency(totals.insurance)}</td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.totalPayment)}</td>
                </>
              )}

              {activeTab === 'payment' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.income)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-red-600 dark:text-red-400">{formatCurrency(totals.taxAmount)}</td>
                  {(formData.includeInsurance || formData.fiscalRole === 'entrepreneur') && (
                    <td className="px-3 py-2 whitespace-nowrap text-right text-amber-600 dark:text-amber-400">{formatCurrency(totals.insurance)}</td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">-</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 12 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<CalendarDays className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} месяцев
          </Button>
        </div>
      )}

      {/* Дополнительная информация о сроках уплаты */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2">Информация о сроках уплаты налогов:</h4>
        {results.taxSystem === 'npd' ? (
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              • Налог на профессиональный доход рассчитывается автоматически в приложении «Мой налог»
            </p>
            <p>
              • Срок уплаты — до 25 числа следующего месяца
            </p>
            <p>
              • Если сумма налога менее 100 рублей, она добавляется к сумме налога в следующем месяце
            </p>
          </div>
        ) : (
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              • Авансовые платежи по УСН за I квартал — до 25 апреля
            </p>
            <p>
              • Авансовые платежи по УСН за II квартал — до 25 июля
            </p>
            <p>
              • Авансовые платежи по УСН за III квартал — до 25 октября
            </p>
            <p>
              • Налог за год для ИП — до 25 апреля следующего года
            </p>
            {formData.fiscalRole === 'entrepreneur' && (
              <p>
                • Фиксированные страховые взносы ИП — до 31 декабря текущего года
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsSchedule;